import { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";

/**
 * Quick-search bar for the most common admin tables.
 * Shows a clean form with a few key fields (e.g., ID_Cliente, NomeCompleto, Email).
 * On submit, applies the values directly to the table filters.
 *
 * Config: array of fields with `col`, `label`, `type` (text|number).
 */
const QUICK_SEARCH_CONFIG = {
  "cliente.Tinf_Cliente": {
    title: "Pesquisa rápida de cliente",
    fields: [
      { col: "ID_Cliente", label: "ID Cliente", type: "number", placeholder: "Ex: 12345" },
      { col: "NomeCompleto", label: "Nome completo", type: "text", placeholder: "Ex: Maria Silva", grow: true },
      { col: "Email", label: "Email", type: "text", placeholder: "Ex: maria@dominio.pt", grow: true },
    ],
  },
  "produto.Tinf_Produto": {
    title: "Pesquisa rápida de produto",
    fields: [
      { col: "ID_Produto", label: "ID Produto", type: "number", placeholder: "Ex: 42" },
      { col: "Descricao", label: "Descrição", type: "text", placeholder: "Ex: Coaching Practitioner", grow: true },
    ],
  },
};

export function getQuickSearchConfig(schema, name) {
  return QUICK_SEARCH_CONFIG[`${schema}.${name}`] || null;
}

export default function AdminQuickSearch({ config, currentFilters, onApply, onClear }) {
  const [values, setValues] = useState({});
  const initialMount = useRef(true);

  // Sync from external filters (e.g., URL prefilters or "Limpar filtros")
  useEffect(() => {
    const seed = {};
    for (const f of config.fields) {
      if (currentFilters?.[f.col] != null) seed[f.col] = currentFilters[f.col];
    }
    setValues(seed);
  }, [config, currentFilters]);

  // Auto-clear local fields when external filters reset entirely
  useEffect(() => {
    if (initialMount.current) { initialMount.current = false; return; }
    if (!currentFilters || Object.keys(currentFilters).length === 0) setValues({});
  }, [currentFilters]);

  const submit = (e) => {
    e?.preventDefault?.();
    const cleaned = {};
    for (const [k, v] of Object.entries(values)) {
      if (v != null && String(v).trim() !== "") cleaned[k] = String(v).trim();
    }
    onApply(cleaned);
  };

  const clear = () => {
    setValues({});
    onClear();
  };

  return (
    <form onSubmit={submit} data-testid="admin-quicksearch"
      className="mb-3 p-3 bg-gradient-to-r from-[#FAF8F2] to-white border border-[#E6E1D6] rounded-md">
      <div className="flex items-center gap-2 mb-2">
        <Search size={14} className="text-[#0F1829]" />
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#0F1829] font-bold">{config.title}</span>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {config.fields.map((f) => (
          <label key={f.col} className={`flex flex-col gap-1 ${f.grow ? "flex-1 min-w-[180px]" : "w-32"}`}>
            <span className="text-[10px] uppercase tracking-[0.12em] text-[#978D78] font-bold">{f.label}</span>
            <input
              type={f.type}
              value={values[f.col] ?? ""}
              placeholder={f.placeholder}
              data-testid={`quicksearch-${f.col}`}
              onChange={(e) => setValues((p) => ({ ...p, [f.col]: e.target.value }))}
              className="px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none bg-white"
            />
          </label>
        ))}
        <div className="flex items-center gap-1.5 pb-px">
          <button type="submit" data-testid="quicksearch-submit"
            className="inline-flex items-center gap-1 px-4 py-1.5 text-xs font-bold uppercase tracking-wider bg-[#0F1829] text-white rounded hover:bg-black">
            <Search size={12} /> Pesquisar
          </button>
          {Object.keys(values).some((k) => values[k]) && (
            <button type="button" onClick={clear} data-testid="quicksearch-clear"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]">
              <X size={11} /> Limpar
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
